import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Footer } from '../Footer';
import styles from './ProductPages.module.css';
import { Video, MessageSquare, TrendingUp, Database, CheckCircle2, ArrowRight } from 'lucide-react';
// Product 1 images
import product11 from './product images/product 1.1.png';
import product12 from './product images/product 1.2.png';
import product13 from './product images/product 1.3.png';
// Product 2 images
import product21 from './product images/product 2.1.png';
import product22 from './product images/product 2.2.png';
import product23 from './product images/product 2.3.png';
// Product 3 images
import product31 from './product images/product 3.1.png';
import product32 from './product images/product 3.2.png';
import product33 from './product images/product 3.3.png';
// Product 4 images
import product41 from './product images/product 4.1.png';
import product42 from './product images/product 4.2.png';
import product43 from './product images/product 4.3.png';
// Legacy images (keeping for compatibility)
import diHealthImg from '../../../../assets/landing/Dashboard.jpg';
import diForecastImg from '../../../../assets/landing/realtime.jpg';
import diSmbImg from '../../../../assets/landing/laptop.png';
import crmPipelineImg from '../../../../assets/landing/products/product 1 img.png';
import crmSyncImg from '../../../../assets/landing/products/product 2 img.png';
import crmSimplicityImg from '../../../../assets/landing/products/product 3 img.png';

const productData = {
    'meeting-assistant': {
        title: 'Unified Meeting Assistant',
        icon: <Video size={ 48 } />,
        color: '#3b82f6',
        images: [product11, product12, product13],
        headline: 'One AI assistant for every sales meeting',
        subheadline: 'Automatically record, transcribe, summarize, and organize your calls, demos, and discussions — across phone, Zoom, Google Meet, and in-person conversations.',
        problem: {
            title: 'The Problem with Sales Meetings Today',
            description: 'Sales meetings are where real decisions happen — but the information rarely survives the call.',
            repStruggles: {
                title: 'Reps struggle with:',
                points: [
                    'Taking notes while presenting',
                    'Forgetting key customer objections',
                    'Missing follow-up commitments',
                    'Updating CRM after long days',
                    'Searching through scattered recordings later'
                ]
            },
            managerStruggles: {
                title: 'Managers face:',
                points: [
                    'Zero visibility into real conversations',
                    'No consistent way to review deals',
                    'Slow onboarding for new reps'
                ]
            },
            result: 'Important insights are lost the moment the meeting ends.'
        },
        solution: {
            title: 'Meet the Unified Meeting Assistant',
            description: 'Galile\'s Unified Meeting Assistant captures every conversation automatically and turns it into clear, structured intelligence your team can act on instantly.',
            tagline: 'No manual notes. No missed details. No extra admin work.',
            features: [
                {
                    title: 'Capture Every Conversation, Everywhere',
                    description: 'Automatically record and organize:',
                    points: ['Phone calls', 'Zoom meetings', 'Google Meet calls', 'In-person discussions via mobile capture'],
                    footer: 'All stored securely in one searchable workspace.'
                },
                {
                    title: 'Instant Transcripts and Structured Summaries',
                    description: 'After each meeting, Galile generates:',
                    points: ['Accurate transcripts', 'Concise summaries', 'Key discussion points', 'Agreed next steps', 'Follow-up reminders'],
                    footer: 'So reps can focus on selling instead of documentation.'
                },
                {
                    title: 'Built for Real-World Sales Workflows',
                    description: 'Unlike generic note-taking tools, Galile is designed for:',
                    points: ['Sales discovery calls', 'Product demos', 'Pricing negotiations', 'Field discussions', 'Renewal conversations'],
                    footer: 'Turning meetings into pipeline intelligence, not just notes.'
                }
            ]
        },
        benefits: {
            title: 'Benefits Teams Experience Immediately',
            points: [
                'Faster follow-ups after meetings',
                'Fewer missed action items',
                'Better meeting recall across teams',
                'Reduced manual note-taking time',
                'Cleaner CRM updates'
            ]
        },
        ctaText: 'Stop losing insights from your sales meetings. Let AI handle the capture, notes, and follow-ups.'
    },
    'conversation-intelligence': {
        title: 'Conversation Intelligence',
        icon: <MessageSquare size={ 48 } />,
        color: '#8b5cf6',
        images: [product21, product22, product23],
        headline: 'Turn every sales conversation into actionable intelligence',
        subheadline: 'Go beyond recordings and summaries — uncover intent, objections, risks, and next steps that actually drive revenue.',
        problem: {
            title: 'Why Recordings Alone Aren\'t Enough',
            description: 'Most tools stop at:',
            repStruggles: {
                title: '',
                points: ['Call recordings', 'Basic transcripts', 'Simple summaries']
            },
            managerStruggles: {
                title: 'But recordings don\'t tell you:',
                points: [
                    'Why deals stall',
                    'What customers really care about',
                    'Which reps perform best',
                    'Which conversations lead to wins'
                ]
            },
            result: 'Without intelligence, sales teams stay reactive instead of data-driven.'
        },
        solution: {
            title: 'From Conversations to Revenue Signals',
            description: 'Galile analyzes every interaction to surface:',
            tagline: 'So your team understands not just what was said — but what it means for the deal.',
            features: [
                {
                    title: 'Real-Time Visibility for Sales Leaders',
                    description: 'Managers can:',
                    points: [
                        'See which deals are at risk',
                        'Identify stalled conversations early',
                        'Compare performance across reps',
                        'Review key moments instantly',
                        'Coach using real customer interactions'
                    ],
                    footer: 'Turning pipeline reviews into fact-based decisions, not opinions.'
                },
                {
                    title: 'Accelerate Rep Performance and Coaching',
                    description: 'Conversation intelligence helps teams:',
                    points: [
                        'Standardize winning talk tracks',
                        'Improve discovery quality',
                        'Handle objections consistently',
                        'Ramp new hires faster',
                        'Increase conversion across stages'
                    ]
                },
                {
                    title: 'Uncover Revenue Signals',
                    description: 'Automatically detect:',
                    points: [
                        'Customer intent and urgency',
                        'Pain points and priorities',
                        'Pricing objections',
                        'Competitor mentions',
                        'Decision timelines',
                        'Commitment signals'
                    ]
                }
            ]
        },
        benefits: {
            title: 'Business Outcomes',
            points: [
                'Higher win rates',
                'Faster deal progression',
                'Improved coaching effectiveness',
                'Better forecast accuracy',
                'Clear visibility into customer intent'
            ]
        },
        ctaText: 'Understand every deal before it\'s won or lost.'
    },
    'deal-intelligence': {
        title: 'Deal Intelligence & Forecasting',
        icon: <TrendingUp size={ 48 } />,
        color: '#10b981',
        images: [product31, product32, product33],
        headline: 'Know which deals will close — and which won\'t',
        subheadline: 'Turn sales conversations, activity signals, and pipeline behavior into clear deal health insights and accurate revenue forecasts — without manual guesswork.',
        problem: {
            title: 'The Forecasting Problem Every Sales Team Faces',
            description: 'Most sales forecasts are based on:',
            repStruggles: {
                title: '',
                points: [
                    'Rep opinions',
                    'Incomplete CRM notes',
                    'Outdated deal activity',
                    'Last-minute updates before review meetings'
                ]
            },
            managerStruggles: {
                title: 'As a result:',
                points: [
                    'Deals slip unexpectedly',
                    'Revenue targets are missed',
                    'Leaders lose confidence in pipeline numbers',
                    'Forecast reviews become debates, not decisions'
                ]
            },
            result: 'Traditional forecasting looks backward. Revenue intelligence looks ahead.'
        },
        solution: {
            title: 'From Conversations to Predictable Revenue',
            description: 'Galile connects what customers say with how deals move to surface real closing signals.',
            tagline: 'By analyzing meetings, follow-ups, objections, and engagement — Galile identifies which deals are healthy, stalled, or at risk.',
            features: [
                {
                    title: 'Deal Health Based on Evidence, Not Optimism',
                    description: 'Each opportunity is enriched with:',
                    points: [
                        'Buying intent signals',
                        'Risk indicators and objections',
                        'Stakeholder engagement levels',
                        'Activity gaps and delays',
                        'Momentum trends over time'
                    ],
                    footer: 'Giving leaders early warning before deals slip.'
                },
                {
                    title: 'Smarter Forecasting Without Spreadsheet Chaos',
                    description: 'Galile helps teams:',
                    points: [
                        'Predict close probability using real signals',
                        'Spot weak pipeline coverage early',
                        'Identify deals likely to slip this quarter',
                        'Understand why forecasts change',
                        'Run fact-based forecast reviews'
                    ],
                    footer: 'Replacing manual roll-ups and subjective updates with data-driven clarity.'
                },
                {
                    title: 'Built for SMB and Mid-Market Reality',
                    description: 'Galile delivers:',
                    points: [
                        'Conversation-driven forecasting',
                        'Fast setup without RevOps',
                        'Affordable per-seat pricing',
                        'Insights usable by founders and managers'
                    ],
                    footer: 'So smaller teams get enterprise-grade predictability without enterprise overhead.'
                }
            ]
        },
        benefits: {
            title: 'Business Outcomes',
            points: [
                'More accurate quarterly forecasts',
                'Earlier detection of slipping deals',
                'Higher close rates through proactive action',
                'Stronger confidence in pipeline reviews',
                'Better alignment between reps, managers, and leadership'
            ]
        },
        ctaText: 'Stop guessing your forecast. Start knowing it.'
    },
    'built-in-crm': {
        title: 'Built-in CRM',
        icon: <Database size={ 48 } />,
        color: '#f59e0b',
        images: [product41, product42, product43],
        headline: 'The built-in CRM to manage your pipeline, activities, and outcomes',
        subheadline: 'Automatically capture conversations, create notes, track follow-ups, and manage deals — without manual data entry from your sales team.',
        problem: {
            title: 'The Problem with Traditional CRMs',
            description: 'Most CRMs depend on reps to:',
            repStruggles: {
                title: '',
                points: [
                    'Write notes after calls',
                    'Update deal stages',
                    'Log follow-ups',
                    'Record meeting outcomes'
                ]
            },
            managerStruggles: {
                title: 'In reality:',
                points: [
                    'Updates are delayed or skipped',
                    'Data becomes incomplete',
                    'Managers lose trust in reports',
                    'Forecasting becomes unreliable'
                ]
            },
            result: 'CRMs fail because manual data entry fails.'
        },
        solution: {
            title: 'Meet the Self-Updating CRM',
            description: 'Galile\'s built-in CRM is powered by real conversations, not manual typing.',
            tagline: 'So your pipeline reflects what actually happened, not what someone remembered to enter.',
            features: [
                {
                    title: 'Automatic Pipeline Updates',
                    description: 'It automatically:',
                    points: [
                        'Creates structured meeting notes',
                        'Logs calls and discussions',
                        'Extracts action items',
                        'Updates deal activity',
                        'Tracks follow-ups'
                    ]
                },
                {
                    title: 'Works Standalone or With Your Existing CRM',
                    description: 'Choose what fits your workflow:',
                    points: [
                        'Use Galile\'s in-built CRM or',
                        'Sync with Zoho',
                        'Sync with HubSpot',
                        'Sync with LeadSquared',
                        'Sync with other CRMs'
                    ],
                    footer: 'No disruption to your existing stack.'
                },
                {
                    title: 'Built for Sales Simplicity, Not Admin Complexity',
                    description: 'Designed to be:',
                    points: [
                        'Easy for reps to adopt',
                        'Fast for founders to understand',
                        'Clean for managers to review',
                        'Reliable for forecasting'
                    ],
                    footer: 'All without RevOps overhead.'
                }
            ]
        },
        benefits: {
            title: 'What Teams Gain',
            points: [
                'Clean, trustworthy pipeline data',
                'Zero manual note-taking burden',
                'Consistent follow-up tracking',
                'Faster onboarding for new reps',
                'More accurate revenue visibility'
            ]
        },
        perfectFor: {
            title: 'Perfect For',
            roles: [
                'SMB sales teams without dedicated RevOps',
                'Founder-led pipelines',
                'Fast-moving inside sales teams',
                'Field teams needing simple reporting'
            ]
        },
        ctaText: 'Finally, a CRM your reps will actually keep updated.'
    }
};

const ProductPages = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const data = productData[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!data) {
        return (
            <div className={ styles.container }>
                <Header />
                <div className={ styles.notFound }>
                    <h1>Product Not Found</h1>
                    <button onClick={ () => navigate('/') }>Return Home</button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ styles.container }>
            <Header />

            <main className={ styles.main }>
                {/* Hero Section */ }
                <section className={ styles.hero } style={ { '--accent-color': data.color } }>
                    <div className={ styles.heroContent }>
                        <div className={ styles.iconBox }>{ data.icon }</div>
                        <h1 className={ styles.headline }>{ data.headline }</h1>
                        <p className={ styles.subheadline }>{ data.subheadline }</p>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButton }>
                                See it in action
                            </button>
                        </a>
                    </div>
                </section>

                {/* Problem Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.problem.title }</h2>
                        <p className={ styles.description }>{ data.problem.description }</p>

                        <div className={ styles.problemContainer }>
                            <div className={ styles.problemGrid }>
                                <div className={ styles.problemCard }>
                                    <div className={ styles.problemCardHeader }>
                                        <div className={ styles.problemCardIcon }>👤</div>
                                        <h3 className={ styles.problemCardTitle }>{ data.problem.repStruggles.title || 'Sales Reps Face' }</h3>
                                    </div>
                                    <ul className={ styles.bulletList }>
                                        { data.problem.repStruggles.points.map((point, i) => (
                                            <li key={ i }>{ point }</li>
                                        )) }
                                    </ul>
                                </div>

                                <div className={ styles.problemCard }>
                                    <div className={ styles.problemCardHeader }>
                                        <div className={ styles.problemCardIcon }>📊</div>
                                        <h3 className={ styles.problemCardTitle }>{ data.problem.managerStruggles.title || 'Sales Managers Face' }</h3>
                                    </div>
                                    <ul className={ styles.bulletList }>
                                        { data.problem.managerStruggles.points.map((point, i) => (
                                            <li key={ i }>{ point }</li>
                                        )) }
                                    </ul>
                                </div>
                            </div>

                            <div className={ styles.resultBox }>
                                <div className={ styles.resultIcon }>💡</div>
                                <p className={ styles.resultText }>{ data.problem.result }</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Solution Section - Zigzag Layout */ }
                <section className={ styles.sectionAlt }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.solution.title }</h2>
                        <p className={ styles.solutionIntro }>{ data.solution.description }</p>
                        { data.solution.tagline && (
                            <p className={ styles.tagline }>{ data.solution.tagline }</p>
                        ) }

                        <div className={ styles.zigzagContainer }>
                            { data.solution.features.map((feature, i) => (
                                <div key={ i } className={ `${styles.zigzagRow} ${i % 2 === 1 ? styles.zigzagRowReverse : ''}` }>
                                    <div className={ styles.zigzagContent }>
                                        <h3 className={ styles.zigzagTitle }>{ feature.title }</h3>
                                        { feature.description && <p className={ styles.zigzagDesc }>{ feature.description }</p> }
                                        <ul className={ styles.zigzagList }>
                                            { feature.points.map((point, j) => (
                                                <li key={ j }>
                                                    <CheckCircle2 size={ 18 } className={ styles.checkIcon } />
                                                    <span>{ point }</span>
                                                </li>
                                            )) }
                                        </ul>
                                        { feature.footer && <p className={ styles.zigzagFooter }>{ feature.footer }</p> }
                                    </div>
                                    <div className={ styles.zigzagImage }>
                                        <div style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            backgroundColor: '#f8fafc', 
                                            borderRadius: '16px',
                                            border: '2px solid #e2e8f0',
                                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0.5rem'
                                        }}>
                                            <img
                                                src={ data.images[i] || data.images[0] }
                                                alt={ feature.title }
                                                style={ {
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                    borderRadius: '12px'
                                                } }
                                            />
                                        </div>
                                    </div>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Benefits Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.benefits.title }</h2>
                        <div className={ styles.outcomesGrid }>
                            { data.benefits.points.map((point, i) => (
                                <div key={ i } className={ styles.outcomeCard }>
                                    <CheckCircle2 size={ 24 } className={ styles.outcomeIcon } />
                                    <span>{ point }</span>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Perfect For Section (if exists) */ }
                { data.perfectFor && (
                    <section className={ styles.sectionAlt }>
                        <div className={ styles.contentWrapper }>
                            <h2 className={ styles.sectionTitle }>{ data.perfectFor.title }</h2>
                            <div className={ styles.rolesList }>
                                { data.perfectFor.roles.map((role, i) => (
                                    <div key={ i } className={ styles.roleItem }>
                                        <span className={ styles.roleBullet }>•</span>
                                        <span>{ role }</span>
                                    </div>
                                )) }
                            </div>
                        </div>
                    </section>
                ) }

                {/* CTA Section */ }
                <section className={ styles.ctaSection }>
                    <div className={ styles.ctaCard }>
                        <h2>{ data.ctaText }</h2>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButtonLarge }>
                                Book a Demo <ArrowRight size={ 20 } />
                            </button>
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ProductPages;
